<template>
    <nav
        v-if="breadcrumbs.length"
        class="flex"
        :class="themeClasses.text"
        aria-label="Breadcrumb"
    >
        <ol class="flex flex-wrap">
            <!-- mobile -->
            <template v-if="!showAllBreadcrumbs">
                <li class="mb-3">
                    <div class="flex items-center leading-tight">
                        <Link
                            :href="route('home')"
                            class="transition-colors"
                            :class="themeClasses.link"
                        >
                            <IconHomeFilled
                                class="w-5 h-5 -mt-0.5 transition-colors"
                                :class="themeClasses.icon"
                            />
                        </Link>

                        <IconAngleLeft
                            class="w-3 h-3 mx-1 rotate-180"
                            :class="themeClasses.separator"
                        />
                    </div>
                </li>

                <li class="mb-3">
                    <div
                        @click.prevent="showAllBreadcrumbs = true"
                        class="rounded h-[18px] w-[40px] flex items-center justify-center gap-1 me-2 transition"
                        :class="themeClasses.dotsWrapper"
                    >
                        <div
                            v-for="i in 3"
                            :key="i"
                            class="h-[5px] w-[5px] rounded-full"
                            :class="themeClasses.dots"
                        />
                    </div>
                </li>
            </template>

            <!-- full -->
            <template v-if="showAllBreadcrumbs">
                <li
                    v-for="(breadcrumb, key) in breadcrumbs"
                    :key="key"
                    class="mb-3"
                >
                    <div class="flex items-center">
                        <IconAngleLeft
                            v-if="key !== 0"
                            class="w-3 h-3 mx-1 rotate-180"
                            :class="themeClasses.separator"
                        />

                        <Link
                            v-if="key !== breadcrumbs.length - 1"
                            :href="breadcrumb.url"
                            class="mt-[1px] text-xs font-bold leading-tight transition"
                            :class="themeClasses.link"
                        >
                            {{ breadcrumb.title }}
                        </Link>

                        <span
                            v-else
                            class="mt-[1px] text-xs font-bold leading-tight"
                            :class="themeClasses.active"
                        >
                            {{ breadcrumb.title }}
                        </span>
                    </div>
                </li>
            </template>

            <!-- mobile current -->
            <template v-else>
                <li
                    v-if="currentBreadcrumb"
                    class="mb-3"
                    @click.prevent="showAllBreadcrumbs = true"
                >
                    <div class="flex items-center gap-1 leading-tight">
                        <IconAngleLeft
                            class="w-3 h-3 mx-1 rotate-180"
                            :class="themeClasses.separator"
                        />

                        <span
                            class="max-md:max-w-[225px] truncate mt-[2px] text-xs font-medium leading-tight transition"
                            :class="themeClasses.link"
                        >
                            {{ currentBreadcrumb.title }}
                        </span>
                    </div>
                </li>
            </template>
        </ol>
    </nav>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { Link, usePage } from '@inertiajs/vue3';
import IconAngleLeft from '@js/components/Icons/IconAngleLeft.vue';
import IconHomeFilled from "@js/components/Icons/IconHomeFilled.vue";

export default {
    name: "Breadcrumbs",
    components: { IconHomeFilled, Link, IconAngleLeft },

    props: {
        theme: {
            type: String,
            default: 'light', // light | dark
        }
    },

    setup(props) {
        const page = usePage();

        const breadcrumbs = ref(page.props.breadcrumbs || []);
        const showAllBreadcrumbs = ref(true);

        const currentBreadcrumb = computed(() => {
            return breadcrumbs.value.at(-1) ?? null;
        });

        const themeClasses = computed(() => {
            if (props.theme === 'dark') {
                return {
                    text: 'text-slate-200',
                    link: 'text-slate-200 hover:text-baseColor',
                    active: 'text-white',
                    icon: 'fill-slate-200 hover:fill-baseColor',
                    separator: 'text-slate-500',
                    dotsWrapper: 'bg-white/10 hover:bg-white/20',
                    dots: 'bg-slate-300',
                };
            }

            // light
            return {
                text: 'text-slate-700',
                link: 'text-slate-700 hover:text-baseColor',
                active: 'text-slate-900',
                icon: 'fill-slate-700 hover:fill-baseColor',
                separator: 'text-slate-400',
                dotsWrapper: 'bg-slate-200 hover:bg-slate-300',
                dots: 'bg-slate-500',
            };
        });

        const updateBreadcrumbsMode = () => {
            if (typeof window !== 'undefined') {
                showAllBreadcrumbs.value = !window.matchMedia('(max-width: 1024px)').matches;
            }
        };

        onMounted(() => {
            updateBreadcrumbsMode();
            window.addEventListener("resize", updateBreadcrumbsMode);
        });

        onBeforeUnmount(() => {
            window.removeEventListener("resize", updateBreadcrumbsMode);
        });

        return {
            breadcrumbs,
            showAllBreadcrumbs,
            currentBreadcrumb,
            themeClasses,
        };
    }
};
</script>
